import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMetadataTagsListQuery } from "entities/game/queries/useMetadataTags";
import { GameInformationTypeEnum } from "entities/game/types";
import React, { useMemo } from "react";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import AutocompleteField from "shared/components/Form/AutocompleteField";
import Select from "shared/components/Form/Select";
import TextField from "shared/components/Form/TextField";
import FieldLabel from "shared/components/Form/components/FieldLabel";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { COLOR_BLACK } from "shared/theme/colors";
import { CheckBoxIcon } from "shared/theme/icons/CheckBoxIcon";
import { CheckedBoxIcon } from "shared/theme/icons/CheckedBoxIcon";
import { useGameDetails } from "../../../../context";
import { useMetadata } from "../../../../stores/useMetadata";
import { RtpExactField } from "./components/RtpExactField";

const GameInformation = () => {
  const { gameDetails } = useGameDetails();

  const { data: metadataTags } = useMetadataTagsListQuery(
    String(gameDetails?.id),
  );

  const rtpExactInput: InputHookResult = useInputState("rtpExact", "");
  const {
    features,
    setFeatures,
    volatility,
    setVolatility,
    rtpExact,
    setRtpExact,
    paylines,
    setPaylines,
    jackpotType,
    setJackpotType,
    languageSupport,
    setLanguageSupport,
    reels,
    setReels,
    rows,
    setRows,
    releaseDate,
    autoplay,
    setAutoplay,
    minStake,
    maxStake,
    maxWin,
    setReleaseDate,
    setMinStake,
    setMaxStake,
    setMaxWin,
  } = useMetadata();

  const getTagsByPrimaryTag = (primaryTag: GameInformationTypeEnum) =>
    metadataTags?.find((tag) => tag.primaryTag === primaryTag)?.secondaryTags ||
    [];

  const selectedRtpExact = useMemo(
    () => rtpExact.map((rtp) => rtp),
    [rtpExact],
  );

  const volatilityTypeOptions = getTagsByPrimaryTag(
    GameInformationTypeEnum.VOLATILITY,
  ).map((item) => ({
    value: item,
    label: item,
  }));

  const autoplayTypeOptions = getTagsByPrimaryTag(
    GameInformationTypeEnum.AUTOPLAY,
  ).map((item) => ({
    value: item,
    label: item,
  }));

  const handleVolatilityChange = (value: string) => {
    setVolatility(value);
  };

  const handleAutoplayChange = (value: string) => {
    setAutoplay(value);
  };

  const handleFeaturesChange = (values: string[]) => {
    const updatedFeatures = values.map((item) => item);
    setFeatures(updatedFeatures);
  };

  const handleJackpotTypeChange = (values: string[]) => {
    const updatedJackpotType = values.map((item) => item);
    setJackpotType(updatedJackpotType);
  };

  const handleLanguageSupportChange = (values: string[]) => {
    const updatedLanguageSupport = values.map((item) => item);
    setLanguageSupport(updatedLanguageSupport);
  };

  return (
    <ContentSection title="GAME INFORMATION">
      <Stack spacing="1rem">
        <AutocompleteField
          multiple
          disableCloseOnSelect
          id="features"
          value={features}
          options={getTagsByPrimaryTag(GameInformationTypeEnum.GAME_FEATURES)}
          onChange={(e, newValue) => {
            handleFeaturesChange(newValue);
          }}
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(option, value) =>
            option && value ? option === value : false
          }
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props} key={props.key}>
                <Checkbox
                  icon={<CheckBoxIcon />}
                  checkedIcon={<CheckedBoxIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  id={props.id}
                />
                <Typography color={COLOR_BLACK} variant="bodyMRegular">
                  {option}
                </Typography>
              </li>
            );
          }}
          renderInput={(params) => (
            <>
              <FieldLabel label="Game features" labelColor={COLOR_BLACK} />
              <TextField {...params} placeholder="Add a tag..." />
            </>
          )}
        />

        <Select
          id="volatility"
          label="Volatility"
          labelColor={COLOR_BLACK}
          placeholder="Select a volatility"
          disabledPlaceholder
          fullWidth
          required
          value={volatility ?? undefined}
          handleChange={handleVolatilityChange}
          options={volatilityTypeOptions}
        />

        <RtpExactField
          id="tag-type-autocomplete"
          value={selectedRtpExact}
          inputProps={rtpExactInput}
          options={[]}
          onChange={(_, value) => {
            setRtpExact(value);
          }}
          onSubmitTagType={() => {
            setRtpExact([...rtpExact, rtpExactInput.input.value]);
            rtpExactInput.setValue("");
          }}
          placeholder="Add a tag..."
        />

        <TextField
          id="paylines"
          label="Paylines"
          labelColor={COLOR_BLACK}
          placeholder="Enter a paylines"
          fullWidth
          type="number"
          value={paylines}
          onChange={(e) => setPaylines(e.target.value)}
        />

        <AutocompleteField
          multiple
          disableCloseOnSelect
          id="jackpotType"
          value={jackpotType}
          options={getTagsByPrimaryTag(GameInformationTypeEnum.JACKPOT_TYPE)}
          onChange={(e, newValue) => {
            handleJackpotTypeChange(newValue);
          }}
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(option, value) =>
            option && value ? option === value : false
          }
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props} key={props.key}>
                <Checkbox
                  icon={<CheckBoxIcon />}
                  checkedIcon={<CheckedBoxIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  id={props.id}
                />
                <Typography color={COLOR_BLACK} variant="bodyMRegular">
                  {option}
                </Typography>
              </li>
            );
          }}
          renderInput={(params) => (
            <>
              <FieldLabel label="Jackpot Type" labelColor={COLOR_BLACK} />
              <TextField {...params} placeholder="Add a tag..." />
            </>
          )}
        />

        <AutocompleteField
          multiple
          disableCloseOnSelect
          id="languageSupport"
          value={languageSupport}
          options={getTagsByPrimaryTag(
            GameInformationTypeEnum.LANGUAGE_SUPPORT,
          )}
          onChange={(e, newValue) => {
            handleLanguageSupportChange(newValue);
          }}
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(option, value) =>
            option && value ? option === value : false
          }
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props} key={props.key}>
                <Checkbox
                  icon={<CheckBoxIcon />}
                  checkedIcon={<CheckedBoxIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  id={props.id}
                />
                <Typography color={COLOR_BLACK} variant="bodyMRegular">
                  {option}
                </Typography>
              </li>
            );
          }}
          renderInput={(params) => (
            <>
              <FieldLabel label="Language support" labelColor={COLOR_BLACK} />
              <TextField {...params} placeholder="Add a tag..." />
            </>
          )}
        />

        <TextField
          id="reels"
          label="Reels"
          labelColor={COLOR_BLACK}
          placeholder="Enter a reels"
          fullWidth
          type="number"
          value={reels}
          onChange={(e) => setReels(e.target.value)}
        />

        <TextField
          id="rows"
          label="Rows"
          labelColor={COLOR_BLACK}
          placeholder="Enter a rows"
          fullWidth
          type="number"
          value={rows}
          onChange={(e) => setRows(e.target.value)}
        />

        <TextField
          id="releaseDate"
          label="Release Date"
          labelColor={COLOR_BLACK}
          placeholder="Enter a release date"
          fullWidth
          type="number"
          value={releaseDate}
          onChange={(e) => setReleaseDate(e.target.value)}
        />

        <Select
          id="autoplay"
          label="Autoplay"
          labelColor={COLOR_BLACK}
          placeholder="Select an autoplay"
          disabledPlaceholder
          fullWidth
          required
          value={autoplay ?? undefined}
          handleChange={handleAutoplayChange}
          options={autoplayTypeOptions}
        />

        <TextField
          id="minStake"
          label="Min Stake"
          labelColor={COLOR_BLACK}
          placeholder="Enter a min stake"
          fullWidth
          value={minStake}
          onChange={(e) => setMinStake(e.target.value)}
        />

        <TextField
          id="maxStake"
          label="Max Stake"
          labelColor={COLOR_BLACK}
          placeholder="Enter a max stake"
          fullWidth
          value={maxStake}
          onChange={(e) => setMaxStake(e.target.value)}
        />

        <TextField
          id="maxWin"
          label="Max Win"
          labelColor={COLOR_BLACK}
          placeholder="Enter a max win"
          fullWidth
          value={maxWin}
          onChange={(e) => setMaxWin(e.target.value)}
        />
      </Stack>
    </ContentSection>
  );
};

export default GameInformation;
